/* eslint-disable no-param-reassign */
import { SyntheticEvent } from 'react';

import { ColorData, AssetTypes } from '@api/v4/assets/assetTypes';
import languagesMap, { LanguageMapEntry, Locale, findCurrentLanguageInMap } from '@components/common/language_menu/languagesMap';
import { getLocalStorage } from '@helpers/local_storage';
import { FlattenedAttachment, InitialModalEditFormContentState, ModalEditFormContentState } from './EditTabTypes';
import { Submit } from './submission';


export const transformColorData = (colorData: ColorData): ColorData => {
  // make sure we submit fully qualified color data
  const transformedColorData = {
    ...colorData
  };

  const selfOrDefault = (key: string, defaultValue: string): void => {
    transformedColorData[key] = colorData[key] || defaultValue;
  };

  const setMissingDefaults = (keyList: string[], defaultValue: string): void => {
    // if at least one of the values exists (is not an empty string), assign the other
    // values that are empty strings to the default value
    if (keyList.reduce((prev, key) => prev || colorData[key], false)) {
      keyList.forEach((key) => selfOrDefault(key, defaultValue));
    }
  };

  setMissingDefaults(["r", "g", "b"], "0");
  setMissingDefaults(["c", "m", "y", "k"], "100");

  if (colorData.hex && colorData.hex.length === 3) {
    // get full hex string from shorthand
    // e.g. #1fe == #11ffee
    let qualifiedHexString = "";
    for (let i = 0; i < 3; i += 1) {
      qualifiedHexString += colorData.hex.substring(i, i + 1).repeat(2);
    }
    transformedColorData.hex = qualifiedHexString;
  }

  return transformedColorData;
};

export const isValidHex = (hex: string): boolean => {
  const hexRegex = /^(?:[0-9A-F]{3}|[0-9A-F]{6})$/i;
  return hexRegex.test(hex);
};

export const isDirty = <T>(a: T | null, b: T): boolean => {
  if (!a && !b) {
    return false;
  }

  if (!(a instanceof Object && b instanceof Object)) {
    if (a !== b) {
      return true;
    }

    return false;
  }

  if (Object.keys(a).length !== Object.keys(b).length) {
    return true;
  }

  let dirty = false;
  if (Object.keys(a).length > 0) {
    dirty = Object.keys(a).reduce((prev, aKey) => prev || a[aKey] !== b[aKey], dirty);
    if (!dirty) {
      dirty = Object.keys(b).reduce((prev, bKey) => prev || a[bKey] !== b[bKey], dirty);
    }
  }

  return dirty;
};

export const getAvailableLanguages = (): LanguageMapEntry[] => {
  const parsedLocales = window.BFG.locales.whitelistedLocales.split(',');
  return languagesMap.filter((languageEntry) => parsedLocales.includes(languageEntry.locale));
};

export const determineUGTLocale = (): Locale | '' => {
  const BFG = window.BFG; // test workaround to use window BFG mock
  if (!BFG.context.hasFeature("translations")) {
    return '';
  }

  const userViewOptionsAll = JSON.parse(getLocalStorage(`userViewOptions_${BFG.currentUser.user_id}`));
  const userViewOptions = userViewOptionsAll && userViewOptionsAll[BFG.resource.key];
  const { staticSiteLocale, ugtLocaleDefault } = BFG.locales;
  const ugtLocaleInLocalStorage = userViewOptions?.selectedUGTLocale;
  const ugtLocaleInLocalStorageInWhitelisted = findCurrentLanguageInMap(ugtLocaleInLocalStorage, getAvailableLanguages());
  const staticSiteLocaleInWhitelisted = findCurrentLanguageInMap(staticSiteLocale, getAvailableLanguages());

  // determine best locale to return
  if (ugtLocaleInLocalStorage && ugtLocaleInLocalStorageInWhitelisted) {
    return ugtLocaleInLocalStorage;
  }

  if (staticSiteLocaleInWhitelisted) {
    return staticSiteLocale;
  }

  return ugtLocaleDefault;
};

export const tempAttachmentTemplate = (file): FlattenedAttachment => ({
  extension: file.filename.substring(file.filename.lastIndexOf(".")).toLowerCase(),
  key: file.external_id,
  temporaryAttachment: true,
  mimetype: file.mimetype,
  filename: file.filename,
  size: file.size,
  url: file.url
});

export const newAssetInitialState = (assetType: AssetTypes): InitialModalEditFormContentState => ({
  asset: null,
  assetKey: '',
  assetType,
  attachments: [],
  customFieldKeys: null,
  customFieldMaps: null,
  printui: null,
  tags: []
});

export const newAssetEmptyEditState = (): ModalEditFormContentState => (
  {
    assetDescription: '',
    assetName: '',
    attachments: [],
    colorData: null,
    customFieldValues: null,
    externalMediumData: null,
    firstAttachmentKey: '',
    personData: null,
    pressData: null,
    tags: [],
    tagSearchSuggestions: null,
    thumbnailUrl: '',
    fontData: null,
    customFieldMaps: null,
    createCustomFieldMaps: [],
    terribleTrumbowygFlag: determineUGTLocale(),
    thumbnailOverride: {},
    watermark: {},
    genericFileData: null
  }
);

export const getFilenameSansExt = (filename: string, extension: string): string => {
  if (extension) {
    const lowercaseFilename = filename.toLowerCase();
    const extIndex = lowercaseFilename.lastIndexOf(`.${extension}`);
    return filename.substring(0, extIndex);
  }

  return filename;
};

export const handleThumbnailError = (
  event: SyntheticEvent<HTMLImageElement, Event>,
  extension: string,
  name: string,
  isLargeView?: boolean,
  isHoverImage?: boolean,
): void => {
  const source = extension
    ? `//static-fast.brandfolder.com/assets/types/${extension}.svg`
    : `//static-fast.brandfolder.com/assets/types/file.svg`;
  const eventTarget = event.target as HTMLImageElement;
  eventTarget.src = source;
  eventTarget.alt = name;

  if (isLargeView) {
    eventTarget.style.cssText = "padding: 25px;";
  }

  // an image inside the attachment item hover preview need a width and height set
  // otherwise the image has a width and height of 0 and 0, and no preview will show
  if (isHoverImage) {
    eventTarget.height = 172;
    eventTarget.width = 172;
  }
};

export const readableSubmissionActions = {
  [Submit.DeletedAsset]: "Delete asset",
  [Submit.DeletedAttachment]: "Delete attachment",
  [Submit.DeletedCustomFieldValue]: "Delete custom field value",
  [Submit.NewAsset]: "Create new asset",
  [Submit.NewCustomFields]: "Create new custom fields",
  [Submit.Tags]: "Update tags",
  [Submit.UpdatedAsset]: "Update asset",
  [Submit.UpdatedAttachment]: "Update attachment",
  [Submit.UpdatedCustomField]: "Update custom field"
};

export const convertSnakeToCamel = (snakeCaseText: string): string => (
  // regex: groups (pairs) of characters with '_' followed by a letter from a-z, case insensitive
  snakeCaseText.replace(/([_][a-z])/ig, (underscoreGroup): string => (
    underscoreGroup.toUpperCase().replace('_', '')
  ))
);
